import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { transformKeysToCamelCase } from '../../utils/caseTransformers';
import { isEnterpriseUser } from '../../utils/user';
import {
  GetTeamsResponse,
  TeamData,
} from '../httpClients/PlatformClient/api.schema';
import { PlatformClient } from '../httpClients/PlatformClient/PlatformClient';
import { QUERY_KEYS } from './consts';
import { useUserQuery } from './useUserQuery';

type Options = Omit<
  UseQueryOptions<GetTeamsResponse, Error, TeamData[]>,
  'queryKey' | 'queryFn' | 'select'
>;

/**
 * Smart hook to fetch teams data based on user's tier level
 * - Enterprise users: Gets all organization teams
 * - Non-enterprise users: Gets only user's teams
 * @returns Query result with appropriate teams data
 */
export const useTeamsQuery = (options: Options = {}) => {
  const { data: user } = useUserQuery();
  const isEnterprise = isEnterpriseUser(user);

  const queryKey = isEnterprise
    ? [QUERY_KEYS.ORGANIZATION_TEAMS]
    : [QUERY_KEYS.USER_TEAMS];

  const queryFn = isEnterprise
    ? PlatformClient.getOrganizationTeams
    : PlatformClient.getUserTeams;

  return useQuery({
    queryKey,
    queryFn,
    select: (data) => transformKeysToCamelCase(data.teams) as TeamData[],
    enabled: !!user,
    ...options,
  });
};
